import {
  Box,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Check } from "lucide-react";
import { FiChevronDown } from "react-icons/fi";
import { memo } from "react";

/**
 * MultiSelectFilter — a compact multi-select filter chip matching the visual
 * language of DropdownSelector (gray label + white value area, 12px Inter),
 * but backed by a Chakra Popover with checkable rows. Used for the PLO-4816
 * reviewer-side campaign filters (application/resource + risk band).
 *
 * Presentational + controlled: the parent owns the selected-value array and the
 * onToggle handler; this only renders and toggles.
 */

export interface MultiSelectOption {
  value: string;
  label: string;
  /** optional leading swatch (e.g. a risk-band colour dot) */
  swatch?: string;
}

export interface MultiSelectFilterProps {
  label: string;
  icon: any;
  options: MultiSelectOption[];
  /** currently-selected values */
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  /** shown in the value area when nothing is selected */
  placeholder?: string;
}

const MultiSelectFilter = memo(
  ({
    label,
    icon,
    options,
    selected,
    onToggle,
    onClear,
    placeholder = "All",
  }: MultiSelectFilterProps) => {
    const count = selected.length;
    const summary =
      count === 0
        ? placeholder
        : count === 1
          ? options.find((o) => o.value === selected[0])?.label ?? "1 selected"
          : `${count} selected`;

    return (
      <Popover placement="bottom-start" gutter={4} isLazy>
        <PopoverTrigger>
          <HStack
            spacing={0}
            cursor="pointer"
            role="button"
            tabIndex={0}
            whiteSpace="nowrap"
          >
            {/* Left — label */}
            <HStack
              px={3}
              py={1.5}
              spacing={2}
              borderRadius="8px 0 0 8px"
              border="1px solid"
              borderColor="#D9E0E3"
              bg="#F0F3F4"
              whiteSpace="nowrap"
            >
              <Icon as={icon} boxSize={3} color="#14242D" />
              <Text
                color="#14242D"
                fontFamily="Inter"
                fontSize="12px"
                fontWeight="500"
                lineHeight="100%"
              >
                {label}
              </Text>
            </HStack>

            {/* Right — value / count */}
            <HStack
              px={3}
              py={1.5}
              spacing={2}
              borderRadius="0 8px 8px 0"
              borderTop="1px solid"
              borderRight="1px solid"
              borderBottom="1px solid"
              borderColor="#D9E0E3"
              bg={count > 0 ? "p.100" : "white"}
              _hover={{ bg: count > 0 ? "p.200" : "gray.50" }}
              whiteSpace="nowrap"
            >
              <Text
                color={count > 0 ? "#4349B8" : "#14242D"}
                fontFamily="Inter"
                fontSize="12px"
                fontWeight={count > 0 ? "600" : "500"}
                lineHeight="100%"
              >
                {summary}
              </Text>
              <Icon as={FiChevronDown} boxSize={3} color="#14242D" />
            </HStack>
          </HStack>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            width="auto"
            minW="200px"
            maxW="320px"
            borderColor="gray.200"
            boxShadow="md"
            _focusVisible={{ outline: "none" }}
          >
            <PopoverBody px={0} py={1} maxH="280px" overflowY="auto">
              {options.length === 0 ? (
                <Text px={3} py={2} fontSize="12px" color="gray.500">
                  No options
                </Text>
              ) : (
                <VStack align="stretch" spacing={0}>
                  {options.map((option) => {
                    const isSelected = selected.includes(option.value);
                    return (
                      <HStack
                        key={option.value}
                        px={3}
                        py={2}
                        spacing={2}
                        cursor="pointer"
                        _hover={{ bg: "gray.50" }}
                        onClick={() => onToggle(option.value)}
                      >
                        <Box
                          w="14px"
                          h="14px"
                          borderRadius="4px"
                          border="1.5px solid"
                          borderColor={isSelected ? "#4349B8" : "#C7CFD3"}
                          bg={isSelected ? "#4349B8" : "white"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          {isSelected && (
                            <Icon as={Check} boxSize="10px" color="white" />
                          )}
                        </Box>
                        {option.swatch && (
                          <Box
                            w="8px"
                            h="8px"
                            borderRadius="full"
                            bg={option.swatch}
                            flexShrink={0}
                          />
                        )}
                        <Text
                          color="#14242D"
                          fontFamily="Inter"
                          fontSize="12px"
                          fontWeight={isSelected ? "600" : "500"}
                          lineHeight="1.2"
                          noOfLines={1}
                        >
                          {option.label}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              )}
              {count > 0 && onClear && (
                <>
                  <Box borderTop="1px solid" borderColor="gray.100" my={1} />
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={onClear}
                  >
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      color="#647884"
                      fontFamily="Inter"
                    >
                      Clear
                    </Text>
                  </HStack>
                </>
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  },
);

MultiSelectFilter.displayName = "MultiSelectFilter";

export default MultiSelectFilter;
